import { useCallback, useEffect, useMemo, useState } from "react";
import ProductSwiper from "../global/swiper/ProductSwiper";
import { getMultiProductsRating } from "@/services/product/reviews";
import { getCookies } from "cookies-next";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { getProductsByCategoryId } from "@/services/category";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

const handlePriceF = (price) => {
  const prices = price
    ?.split("=")
    ?.at(1)
    ?.split(",")
    ?.map((p) => p?.split("-"))
    ?.flat()
    ?.map((n) => +n);
  const [min, max] = [Math.min(...prices), Math.max(...prices)];
  return `minPrice=${min ?? 1}&maxPrice=${max || 80000}`;
};
const formatFilters = (filters) => filters
  ?.split("&")
  ?.map((f) => (f.startsWith("price") ? handlePriceF(f) : f))
  ?.join("&");

const ProductsTabs = ({ data }) => {
  const { t } = useTranslation("home");
  const { handshake } = getCookies();
  const { locale } = useRouter();
  const { cache } = useSWRConfig();
  const [tab, setTab] = useState(data?.value?.[0]?.id);
  const [productRatings, setProductRatings] = useState({});

  const [filters, setFilters] = useState({
    [data?.value?.[0]?.category_id]: [
      {
        categories: [],
        prices: [],
        brands: [],
      },
    ],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    category_filter: [],
    price: [],
    brand: [],
  });

  const tab_products_key = `home-products/${tab}/${locale}`;
  const tab_products_rating_key = `home-products-list/${tab}/${locale}/ratings`;

  // Handles the filter data from API response
  const handleFilterData = (data) => {
    const categories =
      data.find((item) => item.filter_key === "category_filter")
        ?.filterable_values || [];
    const brands =
      data.find((item) => item.filter_key === "brand")?.filterable_values || [];
    const priceAggregate = data?.find((item) => item.filter_key === "price");
    const prices =
      priceAggregate?.filterable_values?.length > 0
        ? priceAggregate.filterable_values
        : [
            { Key: "0-100", Label: "0 - 100" },
            { Key: "100-500", Label: "100 - 500" },
            { Key: "500-1000", Label: "500 - 1000" },
            { Key: "1000-5000", Label: "1000 - 5000" },
            { Key: "5000+", Label: "5000+" },
          ];

    setFilters((prevFilters) => ({
      ...prevFilters,
      [tab]: { categories, prices, brands },
    }));
  };

  // Fetch products for the selected tab and filters
  const {
    trigger: fetchTabProducts,
    data: tabProducts,
    isMutating: isLoadingProducts,
  } = useSWRMutation(
    tab_products_key,
    (_, { arg: { selectedFilters } }) => {
      return getProductsByCategoryId(
        handshake,
        tab,
        20,
        0,
        formatFilters(selectedFilters)
      );
    },
    {
      populateCache: true,
      onSuccess: (res) => {
        // check for price length because it's will have data even no data in EP
        if (!filters?.[tab]?.prices?.length)
          handleFilterData(res?.data?.data?.aggregate);
      },
    }
  );

  const handleFilterChange = useCallback((key, selectedKey) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(selectedKey)
        ? prev[key].filter((info) => info !== selectedKey)
        : [...prev[key], selectedKey],
    }));
  }, []);

  useEffect(() => {
    if (
      selectedFilters?.category_filter ||
      selectedFilters?.brand ||
      selectedFilters?.price
    ) {
      const transformedString = Object.entries(selectedFilters)
        .filter(([, value]) => value != null)
        .map(([key, value]) =>
          value.length > 0
            ? `${key}=${value.toString().replace(/\s+/g, "-")}`
            : null
        )
        ?.filter((info) => !!info)
        .join("&");
      fetchTabProducts({ selectedFilters: transformedString });
    }
  }, [selectedFilters, fetchTabProducts]);

  // Fetch product ratings for the displayed products
  const { trigger: fetchProductRatings, isMutating: isLoadingRatings } =
    useSWRMutation(
      tab_products_rating_key,
      (_, { arg: product_ids }) =>
        getMultiProductsRating(product_ids, handshake),
      {
        populateCache: true,
        onSuccess: (res) => setProductRatings(res?.data?.data || {}),
      }
    );

  useEffect(() => {
    const currentTabCache = cache.get(tab_products_key);

    if (tab && !currentTabCache) {
      fetchTabProducts(tab).then((ret) => {
        const productIds = [
          ...new Set(ret?.data?.data?.products?.flatMap((p) => p.sku)),
        ];
        fetchProductRatings(productIds);
      });
    } else if (tab && currentTabCache) {
      const productIds = [
        ...new Set(
          currentTabCache?.data?.data?.products?.flatMap((p) => p.sku)
        ),
      ];
      const currentTabRatingsCache = cache.get(tab_products_rating_key);

      if (!currentTabRatingsCache) {
        fetchProductRatings(productIds);
      } else {
        setProductRatings(currentTabRatingsCache?.data?.data?.data);
      }
    }
  }, [
    tab,
    fetchTabProducts,
    fetchProductRatings,
    cache,
    locale,
    tab_products_key,
    tab_products_rating_key,
  ]);

  const products = useMemo(() => {
    const activeData = data?.value?.filter((item) => item?.active);
    const currentTabCache = cache.get(tab_products_key);

    const displayedProducts =
      currentTabCache?.data?.data?.data?.products?.length > 0
        ? currentTabCache?.data?.data?.data?.products
        : tabProducts?.data?.data?.products?.length > 0
        ? tabProducts?.data?.data?.products
        : activeData?.at(0)?.products || [];

    const inStockProducts = displayedProducts?.filter(
      (product) => product?.stock?.is_in_stock
    );
    return inStockProducts;
  }, [data?.value, cache, tab_products_key, tabProducts?.data?.data?.products]);

  const enhancedProducts = data.value.map((tab) => ({
    ...tab,
    products: products?.map((product) => ({
      ...product,
      rating:
        productRatings?.length > 0
          ? Object.entries(productRatings?.at(1))
              ?.find((r) => r?.at(0) === product.sku)
              ?.at(1)
          : { count: "0", rating: "0.0" },
    })),
  }));

  return (
    <section className="w-11/12 mx-auto my-4 sm:my-8">
      <ProductSwiper
        data={{ ...data, value: enhancedProducts }}
        handleSwitchTabs={(curTab) => {
          setTab(curTab);
          setSelectedFilters({
            category_filter: [],
            price: [],
            brand: [],
          });
        }}
        isTopCategoryTabsLoading={isLoadingProducts || isLoadingRatings}
        isSubCategoryLoading={
          !selectedFilters.brand.length &&
          !selectedFilters.category_filter.length &&
          !selectedFilters.price.length &&
          (isLoadingProducts || isLoadingRatings)
        }
        t={t}
        filters={filters || {}}
        handleFilterChange={handleFilterChange}
        selectedFilters={selectedFilters}
        tab={tab}
      />
    </section>
  );
};

export default ProductsTabs;
